import React from 'react';
import { Panel } from '../layout/Panel';
import { peerData } from '../../data/terminalData';

const getScoreColor = (score: number): string => {
  if (score >= 85) return 'text-data-green';
  if (score >= 75) return 'text-data-amber';
  return 'text-gray-400';
};

interface PeerPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const PeerPanel: React.FC<PeerPanelProps> = ({ isMinimized, onMinimize }) => {
  // Prepare exportable data
  const exportData = peerData.map(p => ({
    country: p.country,
    gdpGrowth: p.gdp,
    fdi: p.fdi,
    rating: p.rating,
    reserves: p.reserves,
    debtToGDP: p.debt,
    score: p.score,
  }));

  return (
    <Panel id="peers" title="Peer Analysis" subtitle="Regional Comparison" status="static" data={exportData} isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-2">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-terminal-border">
              <th className="text-left py-2 font-medium">COUNTRY</th>
              <th className="text-right py-2 font-medium">GDP%</th>
              <th className="text-right py-2 font-medium">FDI</th>
              <th className="text-center py-2 font-medium">RATING</th>
              <th className="text-right py-2 font-medium">RES</th>
              <th className="text-right py-2 font-medium">DEBT</th>
              <th className="text-center py-2 font-medium">SCORE</th>
            </tr>
          </thead>
          <tbody>
            {peerData.map((p) => {
              const isUZB = p.country === 'UZB';
              return (
                <tr
                  key={p.country}
                  className={`border-b border-terminal-border/30 transition-colors ${
                    isUZB
                      ? 'bg-data-amber/10 hover:bg-data-amber/15'
                      : 'hover:bg-data-amber/5'
                  }`}
                >
                  <td className={`py-3 font-semibold ${isUZB ? 'text-data-amber' : 'text-white'}`}>
                    {p.country}
                    {isUZB && <span className="ml-1.5 text-xs text-data-amber">●</span>}
                  </td>
                  <td className={`py-3 text-right font-mono ${isUZB ? 'text-white font-bold' : 'text-gray-300'}`}>
                    {p.gdp}%
                  </td>
                  <td className={`py-3 text-right font-mono ${isUZB ? 'text-data-green font-bold' : 'text-gray-300'}`}>
                    ${p.fdi}B
                  </td>
                  <td className={`py-3 text-center ${isUZB ? 'text-data-amber font-bold' : 'text-gray-300'}`}>
                    {p.rating}
                  </td>
                  <td className={`py-3 text-right font-mono ${isUZB ? 'text-white' : 'text-gray-300'}`}>
                    ${p.reserves}B
                  </td>
                  <td className={`py-3 text-right font-mono ${isUZB ? 'text-white' : 'text-gray-300'}`}>
                    {p.debt}%
                  </td>
                  <td className="py-3 text-center">
                    <span className={`font-mono font-bold text-base ${getScoreColor(p.score)}`}>
                      {p.score}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Comparison insight */}
        <div className="mt-auto pt-3">
          <div className="bg-terminal-bg rounded-lg px-3 py-2">
            <div className="text-xs text-gray-500 uppercase font-medium">Key Insight</div>
            <div className="text-sm text-gray-300 mt-1">
              UZB leads peer group in <span className="text-data-green font-semibold">opportunity score</span> despite lower credit rating
            </div>
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default PeerPanel;
